import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import blogImage5 from '../../src/assets/images/blog5.png';
import blogImage6 from '../../src/assets/images/blog6.png';
import blogImage7 from '../../src/assets/images/blog7.png';
import { Link } from "react-router-dom";
import { getSeoByUrl } from "../api/api";
import { Helmet } from "react-helmet-async";

const BlogDetails = () => {
    const { id } = useParams();
    const [blogData, setBlogData] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [seoData, setSeoData] = useState({});

    // ---------- Fetch Blog Data ----------
    useEffect(() => {
        fetch(`https://web.nutrillion.one/api/web/blog_get?id=${id}`)
            .then((response) => response.json())
            .then((data) => {
                setBlogData(data);
                setRelatedBlogs(data.relatedBlogs || []);
            })
            .catch((error) => console.error("Error fetching data:", error));
    }, [id]);

    // ---------- Fetch SEO Data (blogData load hone ke baad) ----------
    useEffect(() => {
        if (!blogData?.slug) return;

        const fetchSeoData = async () => {
            try {
                const response = await getSeoByUrl(`blog/${blogData.slug}`);
                if (response.success && response.data) {
                    setSeoData(response.data);
                }
            } catch (error) {
                console.error("Error fetching SEO data:", error);
            }
        };
        fetchSeoData();
    }, [blogData?.slug]);

    const blogImages = [blogImage5, blogImage6, blogImage7];

    if (!blogData) {
        return <div>Loading...</div>;
    }

    // ---------- Helper: HTML strip ----------
    const stripHtml = (html) => {
        if (!html) return "";
        return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    };

    // ---------- Parse Schema Markup from SEO API (agar mile) ----------
    let seoApiSchema = null;
    if (seoData.schemaMarkup) {
        try {
            seoApiSchema =
                typeof seoData.schemaMarkup === "string"
                    ? JSON.parse(seoData.schemaMarkup)
                    : seoData.schemaMarkup;
        } catch (e) {
            console.error("Invalid schema markup JSON:", e);
        }
    }

    // ============ FINAL SEO VALUES (Priority: BlogData > SEO API > Fallback) ============

    // Canonical
    const canonicalUrl =
        seoData.canonicalUrl ||
        (blogData.slug
            ? `https://www.nutrillion.one/blog/${blogData.slug}`
            : `https://www.nutrillion.one/blog/${id}`);

    // Title
    const pageTitle =
        seoData.metaTitle ||
        (blogData.title
            ? `${blogData.title} | Nutrillion`
            : "Blog Details | Nutrillion");

    // Description
    const pageDescription =
        seoData.metaDescription ||
        blogData.sub_title ||
        stripHtml(blogData.description).substring(0, 160) ||
        "Read detailed blog posts on nutrition and wellness";

    // Keywords
    const pageKeywords = seoData.metaKeywords || "";

    // Robots
    const pageRobots = seoData.robotsTxt || "index, follow";

    // OG Image
    const ogImageUrl =
        seoData.ogImage ||
        blogData.bannerUrl ||
        blogData.imageUrl ||
        "";

    // OG Type
    const ogType =
        seoData.ogType === "website"
            ? "article"
            : seoData.ogType || "article";

    // OG Title / Description / URL
    const ogTitle = seoData.ogTitle || pageTitle;
    const ogDescription = seoData.ogDescription || pageDescription;
    const ogUrl = seoData.ogUrl || canonicalUrl;

    // Twitter
    const twitterCard = seoData.twitterCard || "summary_large_image";
    const twitterTitle = seoData.twitterTitle || pageTitle;
    const twitterDescription = seoData.twitterDescription || pageDescription;
    const twitterImage = seoData.twitterImage || ogImageUrl;

    // ---------- Schema Markup ----------
    // Agar SEO API se schema aaya hai toh use karo, warna blogData se banao
    const schemaMarkup =
        seoApiSchema ||
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blogData.title,
            "description": pageDescription,
            "url": canonicalUrl,
            "image": ogImageUrl,
            "datePublished": blogData.createdAt,
            "dateModified": blogData.updatedAt || blogData.createdAt,
            "author": {
                "@type": "Person",
                "name": "Dr. Rishabh Verma"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Nutrillion",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.nutrillion.one/static/media/newlogo.bdc9e52c79ec594912a6.png"
                }
            },
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": canonicalUrl
            }
        };

    return (
        <div className="custom-padding xl-screen-padding flex pt-80 pb-80 flex-col items-center text-center pt-20 gap-8">
            <Helmet>
                {/* ================= PRIMARY META ================= */}
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                {pageKeywords && <meta name="keywords" content={pageKeywords} />}
                <meta name="robots" content={pageRobots} />
                <link rel="canonical" href={canonicalUrl} />

                {/* ================= OPEN GRAPH ================= */}
                <meta property="og:type" content={ogType} />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:url" content={ogUrl} />
                {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
                <meta property="og:site_name" content="Nutrillion" />

                {/* ================= TWITTER CARD ================= */}
                <meta name="twitter:card" content={twitterCard} />
                <meta name="twitter:title" content={twitterTitle} />
                <meta name="twitter:description" content={twitterDescription} />
                {twitterImage && <meta name="twitter:image" content={twitterImage} />}

                {/* ================= SCHEMA MARKUP ================= */}
                <script type="application/ld+json">
                    {JSON.stringify(schemaMarkup)}
                </script>
            </Helmet>

            <h2 className="text-md text-[#c2c2c2] font-lato">{blogData.publish_date}</h2>
            <p
                className="text-3xl font-bold client-text"
                dangerouslySetInnerHTML={{ __html: blogData.title }}
            ></p>

            <div className="w-full">
                <img
                    src={blogData.bannerUrl || blogImages[id % blogImages.length]}
                    alt={blogData.title || "Blog Image"}
                    className="w-full h-auto rounded-3xl"
                />
            </div>

            <div className="w-full pb-80">
                {blogData.other && blogData.other.length > 0 && (
                    <div className="pt-10">
                        {blogData.other.map((item, index) => (
                            <div key={index} className="mb-10">
                                {item.image ? (
                                    <div className="w-full flex flex-col lg:flex-row gap-8">
                                        <div className="w-full lg:w-1/2 text-left flex flex-col justify-center">
                                            <h2 className="text-2xl font-bold text-left font-lato">
                                                {item.title}
                                            </h2>
                                            <p
                                                className="text-lg text-[#777f86] text-left pt-6 policy-conten"
                                                dangerouslySetInnerHTML={{ __html: item.description }}
                                            ></p>
                                        </div>

                                        <div className="w-full lg:w-1/2">
                                            <img
                                                src={item.imageUrl || item[id % item.length]}
                                                alt={`Section Image ${index + 1}`}
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full text-left">
                                        <h2 className="text-2xl font-bold text-left font-lato">
                                            {item.title}
                                        </h2>
                                        <p
                                            className="text-lg text-[#777f86] text-left pt-6 policy-conten"
                                            dangerouslySetInnerHTML={{ __html: item.description }}
                                        ></p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <section className="pt-80 pb-80">
                <div className="w-full h-[50vh] flex justify-center items-center flex-col" style={{ background: 'linear-gradient(to right, #fff1c6, #cccccc, #dac587, #f2fded)', borderRadius: '70px', overflow: 'hidden' }}>
                    <div className="text-center px-4">
                        <h1 className="text-xl sm:text-2xl md:text-2xl pb-6 font-semibold text-gray-800 client-text">Connect With Us!</h1>
                        <p className="text-p text-[#6e777e]">Thank you for reading our blog! We hope you found the information helpful and insightful. If you have any questions, thoughts, or would like to learn more about how we can help you achieve your health goals and provide personalized nutrition plans, don’t hesitate to reach out.</p>
                    </div>
                    <div className="flex justify-center items-center w-full px-8 mt-6 space-x-12">
                        <div className="text-left">
                            <p className="text-sm text-black font-semibold">Contact no: <a href="tel:+919557439399" className="text-gray-800 hover:underline">9557439399</a></p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-black font-semibold">Email: <a href="mailto:info@nutrillion.one" className="text-gray-800 hover:underline">info@nutrillion.one</a></p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="w-full text-left pt-20">
                <h2 className="text-2xl font-bold text-left font-lato border-b-2 border-[#cccccc] pb-4">
                    Related Articles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    {relatedBlogs.map((blog) => (
                        blog.slug !== blogData.slug && (
                            <div key={blog.id} className="relative">
                                <img src={blog.imageUrl || blogImage5} alt={blog.title} className="w-full h-56 object-cover" />
                                <h3 className="text-lg font-semibold mt-4 client-text">{blog.title}</h3>
                                <Link to={`/blog/${blog.slug}`} className="text-[#8f762c] font-semibold mt-2 inline-block cursor-pointer">
                                    Read More
                                </Link>
                            </div>
                        )
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BlogDetails;